import { useEffect } from 'react';

import { Auth0Provider } from '@auth0/auth0-react';
import { AuthLayout, Authentication, Loader } from '@bitstopco/bitstop-theme';
import { ErrorBoundary } from 'react-error-boundary';

import { AUTH0_CONFIG, AUTH0_LOGIN_PATH, AUTH0_LOGOUT_PATH } from '@/constants/app/config';
import { auth0ProviderProps } from '@/helpers/auth0';
import { customAuthActions } from '@/helpers/auth0Session';
import { initGoogleScript } from '@/helpers/googleLoader';
import { recordError } from '@/helpers/otel';
import useAuth0SessionSync from '@/hooks/useAuth0SessionSync';
import { MainLayout } from '@/layouts';

import { MAIN_ROUTES, SETTINGS_ROUTES } from '@/constants/app/views';

import ErrorPage from '@/pages/Error';
import LoginV2 from '@/pages/auth/LoginV2';
import LogoutV2 from '@/pages/auth/LogoutV2';

const privateRoutes = [
  {
    element: <MainLayout settings />,
    children: [...SETTINGS_ROUTES],
  },
  {
    element: <MainLayout />,
    children: [...MAIN_ROUTES],
  },
];

// Auth0 Universal Login, kept on its own path so the default /auth/login flow is untouched
const publicAuthRoutes = [
  {
    element: <AuthLayout />,
    children: [
      {
        path: AUTH0_LOGIN_PATH,
        element: <LoginV2 />,
      },
    ],
  },
];

const unguardedAuthRoutes = [
  {
    element: <AuthLayout />,
    children: [
      {
        path: AUTH0_LOGOUT_PATH,
        element: <LogoutV2 />,
      },
    ],
  },
];

const AppRoutes = ({ loginRedirect }: { loginRedirect?: string }) => (
  <Authentication
    customActions={customAuthActions}
    publicRoutes={unguardedAuthRoutes}
    userAuthenticatedRoutes={privateRoutes}
    userUnauthenticatedRoutes={publicAuthRoutes}
    userAuthenticatedRoutesRedirect={loginRedirect}
  />
);

const Auth0Routes = () => {
  const { isRestoring, loginRedirect } = useAuth0SessionSync();

  if (isRestoring) return <Loader />;

  return <AppRoutes loginRedirect={loginRedirect} />;
};

const Router = () => {
  useEffect(() => {
    initGoogleScript();
  }, []);

  return (
    <ErrorBoundary
      fallback={<ErrorPage />}
      onError={(error) => recordError(error, 'react.error_boundary')}
    >
      {AUTH0_CONFIG.domain ? (
        <Auth0Provider {...auth0ProviderProps}>
          <Auth0Routes />
        </Auth0Provider>
      ) : (
        <AppRoutes />
      )}
    </ErrorBoundary>
  );
};

export default Router;
